/**
 * @module builder
 */

import type { OptionsDocument, QuoteDocument, RatesDocument } from 'paintscout'

import { calculate } from '../../../../calculator/calculate'
// Deep import, and to the pure half of the tier swap: swap-tier-products deliberately imports no
// calculator, which is what lets the swap run from in here without closing a cycle.
import { withSelectedTierProducts } from '../../tiered-packages/swap-tier-products'
import { buildSubstrates } from '../../../../calculator/substrates/build-substrates'
import { sanitizeSubstrates } from '../../../../calculator/substrates/sanitize-substrates'
import { generateProductDescription } from '../generate-product-description/generate-product-description'

export function calculateQuote(args?: {
  quote: QuoteDocument
  options: OptionsDocument
  /**
   * Only read by the tier swap below, for the rate's own coverage of a slot's tier product. Pass it
   * wherever a product slot can be added while the quote sits on a non-base tier; elsewhere every
   * slot is already on its tier's product and the swap moves nothing.
   */
  rates?: RatesDocument | null
}): QuoteDocument {
  const { options, rates } = args ?? {}

  // Tiered Packages: a quote sitting on a non-base tier must hold that tier's products, because
  // `tierAdjustment` reads `totals.price` as that tier's price and adds nothing on top. Slots added
  // after the tier was picked arrive on the base product — no add path knows about tiers — so the
  // swap is re-applied here rather than only when the estimator switches packages (ENG-1825).
  // Costs one walk of the product slots, and nothing at all unless the quote has product rules.
  const quote = withSelectedTierProducts({ quote: args?.quote, options, rates })

  const builtSubstrates = buildSubstrates(quote, options)

  const calculatedQuote = calculate(builtSubstrates, options)

  const sanitizedQuote = sanitizeSubstrates(calculatedQuote)

  sanitizedQuote.productString = {
    ...sanitizedQuote.productString,
    default: generateProductDescription({ quote, options })
  }

  // Same deal for the work order's Additional Work description: keep `default` in step with the
  // additional-work items so an estimator's `custom` override is the only thing that ever pins it.
  sanitizedQuote.additionalProductString = {
    ...sanitizedQuote.additionalProductString,
    default: generateProductDescription({ quote, options, section: 'additional' })
  }

  return sanitizedQuote
}
